/**
 * ft-holdings-table
 * Filterable, sortable holdings table for displaying 13F portfolio data.
 */

import { icon } from "../../../shared/icons.js";
import { fetchHoldings, fetchSubmissions } from "../../lib/financeapis.js";
import { exportData, type ExportFormat } from "../../lib/export.js";
import { createLogger } from "../../lib/logger.js";
import {
  getPreviousQuarter,
  getLatestQuarter,
  parseEdgarSubmissions,
  formatQuarterLabel,
} from "../../lib/parsers.js";
import { Filer } from "../../models/filer.js";
import { TIER_LIMITS, type Tier } from "../../../lib/strategies/pricing.js";
import type {
  HoldingRaw,
  HoldingsProvenance,
  ProvenanceFiling,
} from "../../../shared/api-types.js";

const log = createLogger("ft-holdings-table");

type TableState = "idle" | "loading" | "error" | "empty";

interface Holding {
  issuer: string;
  cusip: string;
  marketValue: string;
  shares: string;
  percentage: string;
  titleOfClass?: string;
  putCall?: string;
  _isValid?: boolean;
  _validationError?: string;
  _category?: "new" | "increase" | "decrease" | "exited";
  // Optional delta fields from API (used for auto-calculating category and change amounts)
  value?: number;
  deltaValue?: number;
  deltaShares?: number;
  sshPrnamt?: number;
  // Calculated fields for display
  changeAmount?: string;
  valueChangePercent?: string;
  shareChangePercent?: string;
}

class HoldingsTable extends HTMLElement {
  private holdings: Holding[] = [];
  private filteredHoldings: Holding[] = [];
  private currentSort = { col: "percentage", dir: "desc" };
  private filterQuery = "";
  private activeCategories = new Set<string>([
    "new",
    "increase",
    "decrease",
    "exited",
  ]);
  private abortController = new AbortController();
  private rowTemplate: HTMLTemplateElement | null = null;
  private errorMessage = "Failed to load holdings data";
  private provenance: HoldingsProvenance | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes() {
    return ["state", "tier"];
  }

  get state(): TableState {
    return (this.getAttribute("state") as TableState) || "idle";
  }

  set state(value: TableState) {
    this.setAttribute("state", value);
  }

  connectedCallback(): void {
    log.debug("connected", {
      cik: this.getAttribute("cik"),
      q1: this.getAttribute("q1"),
      q2: this.getAttribute("q2"),
    });
    this.render();
    this.setupEventListeners();
    this.loadHoldings();

    // Listen for quarter selection changes from ft-company-quarter
    document.addEventListener("quarter-change", this.handleQuarterChange);
  }

  // Precondition: quarter-change event with detail.quarter
  // Postcondition: Updates q1/q2 attributes, triggers reload
  private handleQuarterChange = (e: Event): void => {
    const { quarter } = (e as CustomEvent).detail;
    if (!quarter) return;

    log.log("handleQuarterChange", { quarter });
    const q1 = getPreviousQuarter(quarter);
    this.setAttribute("q1", q1);
    this.setAttribute("q2", quarter);
    this.updateQuarterLabel();
    this.loadHoldings();
  };

  attributeChangedCallback(
    name: string,
    oldValue: string,
    newValue: string,
  ): void {
    if (oldValue !== newValue && this.shadowRoot) {
      if (name === "state") {
        this.updateStateDisplay();
      } else if (name === "tier") {
        this.updateExportDropdown();
      }
    }
  }

  // Update export dropdown disabled state in header based on tier
  private updateExportDropdown(): void {
    const dropdown = document.querySelector("#export-dropdown");
    if (dropdown) {
      if (this.exportEnabled) {
        dropdown.removeAttribute("disabled");
      } else {
        dropdown.setAttribute("disabled", "");
      }
    }
  }

  private updateStateDisplay(): void {
    const tbody = this.shadowRoot?.querySelector("tbody");
    if (!tbody) return;

    switch (this.state) {
      case "loading":
        tbody.innerHTML = `<tr><td colspan="8" class="state-cell"><ft-spinner text="Retrieving holdings data..."></ft-spinner></td></tr>`;
        break;
      case "error":
        tbody.innerHTML = `<tr><td colspan="8" class="state-cell error">${this.errorMessage}</td></tr>`;
        break;
      case "empty":
        tbody.innerHTML = `<tr><td colspan="8" class="state-cell">No holdings data available</td></tr>`;
        break;
      case "idle":
        // Data display handled by updateTableRows
        break;
    }
  }

  disconnectedCallback(): void {
    log.debug("disconnected");
    this.abortController.abort();
    document.removeEventListener("quarter-change", this.handleQuarterChange);
  }

  // Public API: Set holdings data directly
  setData(holdings: Holding[]): void {
    log.debug("setData", { count: holdings.length });
    this.holdings = holdings;

    if (holdings.length === 0) {
      this.state = "empty";
      return;
    }

    // Auto-calculate categories and change amounts from delta fields if available
    this.holdings.forEach((h) => {
      if (h.value !== undefined && h.deltaValue !== undefined) {
        // Calculate category
        if (h._category === undefined) {
          h._category = this.calculateCategory(h.value, h.deltaValue);
        }

        // Calculate change amount and value percentage
        if (!h.changeAmount) {
          const q1Value = h.value - h.deltaValue;
          h.changeAmount = this.formatChangeAmount(h.deltaValue);
          h.valueChangePercent = this.formatChangePercent(q1Value, h.value);
        }
      }

      // Calculate share change percentage
      if (
        h.sshPrnamt !== undefined &&
        h.deltaShares !== undefined &&
        !h.shareChangePercent
      ) {
        const q1Shares = h.sshPrnamt - h.deltaShares;
        h.shareChangePercent = this.formatChangePercent(q1Shares, h.sshPrnamt);
      }

      // Default to 'new' if no category assigned
      if (h._category === undefined) {
        h._category = "new";
      }
    });

    this.filteredHoldings = [...holdings];
    this.sortHoldings();
    this.state = "idle";
    this.updateTableRows();
    this.updateFilterCounts();
    this.updateQuarterLabel();
  }

  // Precondition: Holdings have been categorized
  // Postcondition: Sets counts attribute on ft-table-filter
  private updateFilterCounts(): void {
    const counts = {
      new: this.holdings.filter((h) => h._category === "new").length,
      increase: this.holdings.filter((h) => h._category === "increase").length,
      decrease: this.holdings.filter((h) => h._category === "decrease").length,
      exited: this.holdings.filter((h) => h._category === "exited").length,
    };
    const tableFilter = this.shadowRoot?.querySelector("ft-table-filter");
    tableFilter?.setAttribute("counts", JSON.stringify(counts));
  }

  // Precondition: currentValue (Q2) and deltaValue are numbers
  // Postcondition: Returns category: 'new' | 'exited' | 'increase' | 'decrease'
  private calculateCategory(
    currentValue: number,
    deltaValue: number,
  ): "new" | "increase" | "decrease" | "exited" {
    const q1Value = currentValue - deltaValue;

    // Position was exited (sold completely)
    if (currentValue === 0) return "exited";

    // New position (didn't exist in Q1)
    if (q1Value === 0) return "new";

    // Position existed in both periods - check direction
    return currentValue > q1Value ? "increase" : "decrease";
  }

  // Precondition: deltaValue is a number
  // Postcondition: Returns formatted change amount with sign (e.g., "+$1,234,567" or "-$987,654")
  private formatChangeAmount(deltaValue: number): string {
    const sign = deltaValue >= 0 ? "+" : "";
    return `${sign}$${new Intl.NumberFormat("en-US").format(Math.round(deltaValue))}`;
  }

  // Precondition: q1Value and q2Value are numbers
  // Postcondition: Returns formatted percentage change (e.g., "+12.34%" or "-5.67%"), or "—" if invalid
  private formatChangePercent(q1Value: number, q2Value: number): string {
    if (q1Value === 0 || !isFinite(q1Value)) return "—"; // No previous value to compare
    const percentChange = ((q2Value - q1Value) / q1Value) * 100;
    if (!isFinite(percentChange)) return "—"; // Handle NaN or Infinity
    const sign = percentChange >= 0 ? "+" : "";
    return `${sign}${percentChange.toFixed(2)}%`;
  }

  private async loadHoldings(): Promise<void> {
    const cik = this.getAttribute("cik");
    let q1 = this.getAttribute("q1");
    let q2 = this.getAttribute("q2");

    if (!cik) {
      log.warn("loadHoldings skipped: missing cik");
      return;
    }

    // If quarters missing, fetch from filer's latest submission
    if (!q1 || !q2) {
      log.log("loadHoldings: fetching latest quarter", { cik });
      this.state = "loading";

      const subResult = await fetchSubmissions(cik);
      if (!subResult.ok) {
        log.error("loadHoldings: failed to fetch submissions", { cik });
        this.setError("No filings found for this filer");
        return;
      }

      const submissions = parseEdgarSubmissions(subResult.data);
      q2 = getLatestQuarter(submissions);
      if (!q2) {
        log.error("loadHoldings: no 13F filings found", { cik });
        this.setError("No 13F filings found for this filer");
        return;
      }
      q1 = getPreviousQuarter(q2);

      this.setAttribute("q1", q1);
      this.setAttribute("q2", q2);
      log.log("loadHoldings: resolved quarters", { q1, q2 });
    }

    log.log("loadHoldings", { cik, q1, q2 });
    this.state = "loading";

    const result = await fetchHoldings(cik, q1, q2);

    if (!result.ok) {
      log.error("loadHoldings failed", { cik, q1, q2, error: result.error });
      this.setError("Failed to load holdings data");
      return;
    }

    const raw = result.data.holdings || [];
    log.log("loadHoldings success", { count: raw.length });

    // Store provenance for footer display
    if (result.data.provenance) {
      this.setProvenance(result.data.provenance);
    }

    this.setData(this.formatHoldings(raw));
  }

  // Public API: Set provenance data for footer and badge
  setProvenance(provenance: HoldingsProvenance): void {
    this.provenance = provenance;
    this.updateProvenanceFooter();
    this.updateQuarterLabel();

    // Fetch filer name if not in provenance
    if (!provenance.filerName && provenance.cik) {
      this.loadFilerName(provenance.cik);
    }
  }

  // Precondition: cik is valid SEC CIK
  // Postcondition: Filer name fetched and badge updated
  private async loadFilerName(cik: string): Promise<void> {
    const filer = await Filer.find(cik);
    if (!filer || !this.provenance) return;
    this.provenance.filerName = filer.name;
    this.updateQuarterLabel();
  }

  // Precondition: provenance is set
  // Postcondition: tfoot is updated with filing links inside disclosure
  private updateProvenanceFooter(): void {
    const tfoot = this.shadowRoot?.querySelector("tfoot");
    if (!tfoot || !this.provenance) return;

    const { cik, filerName, q1, q2, q1Filings, q2Filings } = this.provenance;
    const cikPadded = cik.padStart(10, "0");

    // Render one canonical ProvenanceFiling: the API supplies form + a ready
    // primaryDocUrl, so we no longer rebuild the SEC URL by hand or guess the
    // form type from array position (ADR-016 CLEAN-001).
    const buildFilingLink = (
      filing: ProvenanceFiling,
      quarter: string,
    ): string => {
      return `
        <div class="filing-row">
          <span class="filing-quarter">${quarter}</span>
          <span class="filing-form">${filing.form}</span>
          <span class="filing-accession">${filing.accession}</span>
          <a href="${filing.primaryDocUrl}" target="_blank" rel="noopener" class="filing-link">SEC.gov ${icon("external-link", 12)}</a>
        </div>
      `;
    };

    // Build all filing rows (Q2 first since it's the current quarter)
    const q2Html = (q2Filings || [])
      .map((filing) => buildFilingLink(filing, q2))
      .join("");
    const q1Html = (q1Filings || [])
      .map((filing) => buildFilingLink(filing, q1))
      .join("");
    const filingsHtml =
      q2Html + q1Html ||
      '<span class="filing-note">Filing details not available</span>';

    const cikUrl = `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${cikPadded}&type=13F&dateb=&owner=include&count=40`;

    tfoot.innerHTML = `
      <tr>
        <td colspan="8">
          <ft-disclosure>
            <span slot="summary" class="provenance-summary-slot">Provenance <span class="info-icon">${icon("info", 14)}</span></span>
            <div class="provenance-content">
              <div class="provenance-header">
                SEC 13F-HR · <a href="${cikUrl}" target="_blank" rel="noopener" class="provenance-cik-link">CIK ${cikPadded}</a>
              </div>
              <div class="provenance-filings">
                ${filingsHtml}
              </div>
              <div class="provenance-disclaimer">
                Values in USD. Quarter-over-quarter changes from most recent filing per period.
              </div>
            </div>
          </ft-disclosure>
        </td>
      </tr>
    `;
  }

  // Precondition: str is a string
  // Postcondition: Returns HTML-safe string
  private escapeHtml(str: string): string {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  private formatHoldings(raw: HoldingRaw[]): Holding[] {
    const totalValue = raw.reduce((sum, h) => sum + (h.value || 0), 0);

    return raw
      .sort((a, b) => (b.value || 0) - (a.value || 0))
      .map((h) => {
        const percentage =
          totalValue > 0
            ? (((h.value || 0) / totalValue) * 100).toFixed(2)
            : "0.00";

        return {
          issuer: h.nameOfIssuer || "Unknown",
          cusip: h.cusip || "",
          marketValue: this.formatCurrency(h.value || 0),
          shares: this.formatNumber(h.sshPrnamt || 0),
          percentage: percentage + "%",
          titleOfClass: h.titleOfClass,
          putCall: h.putCall,
          value: h.value,
          deltaValue: h.deltaValue,
          deltaShares: h.deltaShares,
          sshPrnamt: h.sshPrnamt || 0,
        };
      });
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  private parseNum(str: string): number {
    return (
      parseFloat(str.replace(/,/g, "").replace("%", "").replace("$", "")) || 0
    );
  }

  private sortHoldings(): void {
    this.filteredHoldings.sort((a: any, b: any) => {
      const col = this.currentSort.col as keyof Holding;
      let aVal = a[col];
      let bVal = b[col];

      const numCols = [
        "marketValue",
        "valueChangePercent",
        "changeAmount",
        "shares",
        "shareChangePercent",
        "percentage",
      ];
      if (numCols.includes(this.currentSort.col)) {
        aVal = this.parseNum(aVal as string);
        bVal = this.parseNum(bVal as string);
      } else {
        aVal = ((aVal as string) || "").toLowerCase();
        bVal = ((bVal as string) || "").toLowerCase();
      }

      if (aVal < bVal) return this.currentSort.dir === "asc" ? -1 : 1;
      if (aVal > bVal) return this.currentSort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }

  private applyFilter(): void {
    const query = this.filterQuery.toLowerCase();
    this.filteredHoldings = this.holdings.filter((h) => {
      // Text search filter
      if (query) {
        const matches =
          h.issuer.toLowerCase().includes(query) ||
          h.cusip.toLowerCase().includes(query);
        if (!matches) return false;
      }

      // Category filter (always exists after setData)
      const category = h._category as string;
      if (!this.activeCategories.has(category)) return false;

      return true;
    });
    log.debug("filter", {
      query: this.filterQuery,
      categories: [...this.activeCategories],
      results: this.filteredHoldings.length,
    });
    this.sortHoldings();
  }

  private updateTableRows(): void {
    const tbody = this.shadowRoot?.querySelector("tbody");
    if (!tbody) return;

    // Clear existing rows
    while (tbody.firstChild) {
      tbody.removeChild(tbody.firstChild);
    }

    // Build rows using cloned template
    const fragment = document.createDocumentFragment();
    this.filteredHoldings.forEach((h, i) => {
      const row = this.createRowElement(h, i);
      fragment.appendChild(row);
    });

    tbody.appendChild(fragment);
  }

  private createRowElement(h: Holding, i: number): HTMLTableRowElement {
    if (!this.rowTemplate) return document.createElement("tr");

    // Clone the template
    const tr = this.rowTemplate.content.firstElementChild?.cloneNode(
      true,
    ) as HTMLTableRowElement;

    // Set attributes
    tr.dataset.idx = String(i);
    tr.dataset.cusip = h.cusip;

    // Heatmap: weight-based row highlighting
    const pct = parseFloat(h.percentage);
    if (pct > 10) tr.classList.add("heat-high");
    else if (pct > 5) tr.classList.add("heat-med");

    // Handle blur for rows beyond the visible count (gated content)
    const limit = this.visibleCount;
    if (limit > 0 && i >= limit) {
      tr.classList.add("blurred");
    }

    // Handle invalid state
    if (h._isValid === false) {
      tr.classList.add("invalid-row");
      tr.title = `Invalid: ${h._validationError}`;
    }

    // Get cells and populate them
    const cells = tr.querySelectorAll("td");

    // Map sort column to cell index
    // Order: # | Issuer | Value | Value Δ% | Shares | Shares Δ% | Change $ | % Portfolio
    const colIndexMap: Record<string, number> = {
      issuer: 1,
      marketValue: 2,
      valueChangePercent: 3,
      shares: 4,
      shareChangePercent: 5,
      changeAmount: 6,
      percentage: 7,
    };
    const sortColIndex = colIndexMap[this.currentSort.col];
    if (sortColIndex !== undefined) {
      cells[sortColIndex].classList.add("sort-col");
    }

    // Rank cell
    cells[0].textContent = String(i + 1);

    // Issuer cell (with "New" badge for new positions)
    const issuerCell = cells[1];
    if (h._category === "new") {
      issuerCell.innerHTML =
        this.escapeHtml(h.issuer) + '<span class="badge-new">New</span>';
    } else {
      issuerCell.textContent = h.issuer;
    }
    const putCallLabel =
      h.putCall === "C" ? "Call" : h.putCall === "P" ? "Put" : "";
    const tooltip = [putCallLabel, h.titleOfClass, h.cusip]
      .filter(Boolean)
      .join(" - ");
    if (tooltip) {
      issuerCell.title = tooltip;
    }

    // Determine color class based on delta
    const valueChangeClass =
      h.deltaValue && h.deltaValue > 0
        ? "increase"
        : h.deltaValue && h.deltaValue < 0
          ? "decrease"
          : "";
    const shareChangeClass =
      h.deltaShares && h.deltaShares > 0
        ? "increase"
        : h.deltaShares && h.deltaShares < 0
          ? "decrease"
          : "";

    // Market Value cell
    cells[2].textContent = h.marketValue;

    // Value Change Percent cell
    cells[3].textContent = h.valueChangePercent || "—";
    if (valueChangeClass) cells[3].classList.add(valueChangeClass);

    // Shares cell
    cells[4].textContent = h.shares;

    // Share Change Percent cell
    cells[5].textContent = h.shareChangePercent || "—";
    if (shareChangeClass) cells[5].classList.add(shareChangeClass);

    // Change Amount cell
    cells[6].textContent = h.changeAmount || "—";
    if (valueChangeClass) cells[6].classList.add(valueChangeClass);

    // Percentage cell
    cells[7].textContent = h.percentage;

    return tr;
  }

  private updateSortIndicators(): void {
    this.shadowRoot?.querySelectorAll("th.sortable").forEach((th) => {
      const col = th.getAttribute("data-col");
      const icon = th.querySelector(".sort-icon");

      th.classList.remove("sort-asc", "sort-desc");

      if (col === this.currentSort.col) {
        const isAsc = this.currentSort.dir === "asc";
        th.classList.add(isAsc ? "sort-asc" : "sort-desc");
        th.setAttribute("aria-sort", isAsc ? "ascending" : "descending");
        if (icon) icon.textContent = isAsc ? "↑" : "↓";
      } else {
        th.setAttribute("aria-sort", "none");
        if (icon) icon.textContent = "↕";
      }
    });
  }

  // Public API: Set loading state
  setLoading(message?: string): void {
    if (message) {
      this.errorMessage = message; // Reuse for custom loading message
    }
    this.state = "loading";
  }

  // Public API: Set error state
  setError(message?: string): void {
    this.errorMessage = message || "Failed to load holdings data";
    log.error("setError", { message: this.errorMessage });
    this.state = "error";
  }

  // Public API: Set empty state
  setEmpty(message?: string): void {
    if (message) this.errorMessage = message;
    this.state = "empty";
  }

  // Public API: Clear error and return to idle
  clearError(): void {
    this.state = this.holdings.length > 0 ? "idle" : "empty";
  }

  private toggleCategory(category: string): void {
    if (this.activeCategories.has(category)) {
      this.activeCategories.delete(category);
    } else {
      this.activeCategories.add(category);
    }

    const button = this.shadowRoot?.querySelector(
      `[data-category="${category}"]`,
    ) as HTMLButtonElement;
    if (button) {
      button.classList.toggle("active");
    }

    this.applyFilter();
    this.updateTableRows();
  }

  private setupEventListeners(): void {
    const signal = this.abortController.signal;
    const thead = this.shadowRoot?.querySelector("thead");
    const tableFilter =
      this.shadowRoot?.querySelector<FtTableFilter>("ft-table-filter");
    const exportDropdown = document.querySelector("#export-dropdown");

    // Listen to ft-table-filter changes
    tableFilter?.addEventListener(
      "filter-change",
      ((e: CustomEvent) => {
        const { searchQuery, activeCategories } = e.detail;
        this.filterQuery = searchQuery;
        this.activeCategories = activeCategories;
        this.applyFilter();
        this.updateTableRows();
      }) as EventListener,
      { signal },
    );

    // Listen to export dropdown selection (in header)
    exportDropdown?.addEventListener(
      "change",
      ((e: CustomEvent) => {
        const format = e.detail?.value || "csv";
        this.handleExport(format as ExportFormat);
      }) as EventListener,
      { signal },
    );

    // Handle table sorting via click
    thead?.addEventListener(
      "click",
      (e) => {
        const th = (e.target as Element).closest("th.sortable");
        if (th) this.handleSort(th);
      },
      { signal },
    );

    // Handle table sorting via keyboard
    thead?.addEventListener(
      "keydown",
      (e) => {
        const key = (e as KeyboardEvent).key;
        if (key === "Enter" || key === " ") {
          const th = (e.target as Element).closest("th.sortable");
          if (th) {
            e.preventDefault();
            this.handleSort(th);
          }
        }
      },
      { signal },
    );
  }

  // Precondition: filteredHoldings is populated
  // Postcondition: Triggers download of current holdings data in specified format
  private handleExport(format: ExportFormat = "csv"): void {
    const data = this.prepareExportData();
    const filename = this.getExportFilename();
    log.log("handleExport", { count: data.length, filename, format });
    exportData(data, filename, format, {
      columns: [
        "issuer",
        "cusip",
        "market_value",
        "shares",
        "percentage",
        "value_change_pct",
        "share_change_pct",
        "change_amount",
        "category",
      ],
    });
  }

  // Precondition: filteredHoldings contains Holding objects
  // Postcondition: Returns flat export-friendly records
  private prepareExportData(): Record<string, unknown>[] {
    return this.filteredHoldings.map((h) => ({
      issuer: h.issuer,
      cusip: h.cusip,
      market_value: this.parseNum(h.marketValue),
      shares: this.parseNum(h.shares),
      percentage: this.parseNum(h.percentage),
      value_change_pct: h.valueChangePercent
        ? this.parseNum(h.valueChangePercent)
        : null,
      share_change_pct: h.shareChangePercent
        ? this.parseNum(h.shareChangePercent)
        : null,
      change_amount: h.changeAmount ? this.parseNum(h.changeAmount) : null,
      category: h._category || "",
    }));
  }

  // Precondition: cik and q2 attributes may be set
  // Postcondition: Returns descriptive filename for export
  private getExportFilename(): string {
    const cik = this.getAttribute("cik") || "holdings";
    const q2 = this.getAttribute("q2") || "";
    return `${cik}-holdings-${q2}`;
  }

  // Precondition: tier attribute is set
  // Postcondition: Returns true if export is enabled for current tier
  private get exportEnabled(): boolean {
    const tier = (this.getAttribute("tier") || "anonymous") as Tier;
    return TIER_LIMITS[tier]?.exportsEnabled ?? false;
  }

  // Returns number of visible holdings (0 = unlimited)
  // All holdings are visible to all users - no tier gating
  private get visibleCount(): number {
    return 0;
  }

  // Precondition: shadowRoot exists
  // Postcondition: Updates or creates badge above table with filer name + quarter
  private updateQuarterLabel(): void {
    const container = this.shadowRoot?.querySelector(".container");
    const tblWrap = this.shadowRoot?.querySelector(".tbl-wrap");
    if (!container || !tblWrap) return;

    const quarterLabel = formatQuarterLabel(this.getAttribute("q2"));
    const filerName = this.provenance?.filerName;
    const badgeText =
      filerName && quarterLabel ? `${filerName} ${quarterLabel}` : quarterLabel;

    let badgeEl = container.querySelector(
      ".component-badge",
    ) as HTMLSpanElement | null;

    if (badgeText) {
      if (!badgeEl) {
        badgeEl = document.createElement("span");
        badgeEl.className = "component-badge";
        container.insertBefore(badgeEl, tblWrap);
      }
      badgeEl.textContent = badgeText;
    } else if (badgeEl) {
      badgeEl.remove();
    }
  }

  private handleSort(th: Element): void {
    const col = th.getAttribute("data-col") || "";
    if (this.currentSort.col === col) {
      this.currentSort.dir = this.currentSort.dir === "asc" ? "desc" : "asc";
    } else {
      this.currentSort.col = col;
      this.currentSort.dir = "asc";
    }

    log.debug("sort", { col: this.currentSort.col, dir: this.currentSort.dir });
    this.sortHoldings();
    this.updateTableRows();
    this.updateSortIndicators();
  }

  private render(): void {
    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    // Create row template for cloning
    this.rowTemplate = document.createElement("template");
    this.rowTemplate.innerHTML = `
      <tr>
        <td class="rank"></td>
        <td class="issuer"></td>
        <td class="num"></td>
        <td class="num"></td>
        <td class="num shares"></td>
        <td class="num"></td>
        <td class="num"></td>
        <td class="num"></td>
      </tr>
    `;

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        --_font-body: "Space Grotesk", system-ui, sans-serif;
        --_font-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        --_panel: #111111;
        --_surface: #0a0a0a;
        --_border: #161616;
        --_border-accent: #1f1f1f;
        --_text: #f0f0f0;
        --_text-secondary: #a0a0a0;
        --_text-dim: #525252;
        --_text-faint: #3a3a3a;
        --_accent: #c9a84c;
        --_green: #22c55e;
        --_red: #ef4444;
        --_green-bg: rgba(34, 197, 94, 0.12);
        --_red-bg: rgba(239, 68, 68, 0.12);
        --_focus: #E0524D;
      }

      @media (prefers-color-scheme: light) {
        :host {
          --_panel: #ffffff;
          --_surface: #f4f4f5;
          --_border: #e2e4e8;
          --_border-accent: #d1d5db;
          --_text: #1c1c1e;
          --_text-secondary: #666666;
          --_text-dim: #888888;
          --_text-faint: #bbbbbb;
        }
      }

      * { margin: 0; padding: 0; box-sizing: border-box; }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .container { width: 100%; position: relative; }

      ft-table-filter { margin-bottom: 0.75em; }

      .tbl-wrap {
        border: 1px solid var(--_border);
        border-radius: 6px;
        overflow: auto;
        background: var(--_surface);
      }

      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        min-width: 900px;
        font-variant-numeric: tabular-nums;
      }

      /* ── Header ── */
      thead th {
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--_panel);
        border-bottom: 1px solid var(--_border);
        text-align: left;
        font: 600 0.625rem/1.2 var(--_font-body);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--_text-dim);
        padding: 0.5em 0.625em;
      }

      th.sortable {
        cursor: pointer;
        user-select: none;
        transition: color 0.15s ease;
      }

      th.sortable:hover { color: var(--_text); }

      th.sortable:focus-visible {
        outline: 2px solid var(--_focus);
        outline-offset: 2px;
      }

      th .sort-icon {
        opacity: 0.4;
        font-size: 0.5rem;
        margin-left: 0.25em;
      }

      th.sort-asc .sort-icon,
      th.sort-desc .sort-icon { opacity: 1; }

      th.sort-asc,
      th.sort-desc { font-weight: 700; color: var(--_text); }

      td.sort-col { font-weight: 700; }
      th.num { text-align: right; }

      /* ── Body ── */
      tbody td {
        padding: 0.5em 0.625em;
        border-bottom: 1px solid var(--_border);
        color: var(--_text);
        font: 400 0.75rem/1.2 var(--_font-body);
      }

      tbody tr:last-child td { border-bottom: none; }

      tbody tr:nth-child(even):not(.heat-high):not(.heat-med) td {
        background: rgba(255, 255, 255, 0.015);
      }

      tbody tr:hover td { background: var(--_panel); }
      tbody tr[hidden] { display: none; }
      tbody tr.invalid-row { opacity: 0.6; }

      tbody tr.blurred td {
        filter: blur(5px);
        user-select: none;
        pointer-events: none;
        opacity: 0.7;
      }

      tbody tr.blurred:hover td {
        filter: blur(5px);
        background: transparent;
        opacity: 0.7;
      }

      .blur-overlay {
        position: absolute;
        top: calc(var(--toolbar-height, 52px) + 42px + var(--visible-rows-height, 200px));
        left: 0;
        right: 0;
        height: 240px;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        z-index: 2;
        pointer-events: none;
        padding: 1.5em 1em 0;
      }

      .blur-overlay ft-signup-cta {
        pointer-events: auto;
        margin: 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        border-radius: 12px;
        width: 560px;
      }

      /* ── Numeric cells ── */
      td.num {
        text-align: right;
        white-space: nowrap;
        font: 500 0.75rem/1.2 var(--_font-mono);
        font-variant-numeric: tabular-nums slashed-zero;
        font-feature-settings: "tnum" 1, "zero" 1;
      }

      td.num.increase { color: var(--_green); }
      td.num.decrease { color: var(--_red); }

      /* ── Shares column (dimmer) ── */
      td.shares {
        color: var(--_text-dim);
        font-size: 0.6875rem;
      }

      /* ── Rank column ── */
      .rank {
        color: var(--_text-faint);
        font-size: 0.5rem;
        text-align: center;
        width: 1.5rem;
      }

      /* ── Heatmap ── */
      tbody tr.heat-high td { background: rgba(201, 168, 76, 0.08); }
      tbody tr.heat-med td { background: rgba(201, 168, 76, 0.04); }

      /* ── Issuer cell ── */
      .issuer {
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 280px;
      }

      /* ── New badge ── */
      .badge-new {
        display: inline-block;
        font-size: 0.4375rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--_green);
        background: var(--_green-bg);
        padding: 0.1em 0.3em;
        border-radius: 2px;
        margin-left: 0.375em;
        vertical-align: middle;
      }

      td.state-cell {
        text-align: center;
        color: var(--_text-dim);
        padding: 2em 1em;
      }

      td.state-cell.error { color: var(--_red); }

      /* ── Responsive ── */
      /* Column order: 1-# | 2-Issuer | 3-Value | 4-Value Δ% | 5-Shares | 6-Shares Δ% | 7-Change $ | 8-% Portfolio */
      @media (max-width: 768px) {
        /* Mobile: Hide #(1), Shares(5), Shares Δ%(6), Change $(7) */
        thead th:nth-child(1),
        thead th:nth-child(5),
        thead th:nth-child(6),
        thead th:nth-child(7),
        tbody td:nth-child(1),
        tbody td:nth-child(5),
        tbody td:nth-child(6),
        tbody td:nth-child(7) {
          display: none;
        }

        table { min-width: unset; width: 100%; }

        .issuer { max-width: 140px; }
      }

      @media (min-width: 769px) and (max-width: 1024px) {
        /* Tablet: Hide #(1), Change $(7) */
        thead th:nth-child(1),
        thead th:nth-child(7),
        tbody td:nth-child(1),
        tbody td:nth-child(7) {
          display: none;
        }

        table { min-width: unset; width: 100%; }

        .issuer { max-width: 200px; }
      }

      @media (min-width: 1025px) {
        table { min-width: 1000px; }
      }

      /* ── Provenance Footer ── */
      tfoot { background: var(--_panel); }

      tfoot td {
        padding: 0.5em 0.625em;
        border-top: 1px solid var(--_border);
      }

      tfoot ft-disclosure::part(summary) {
        padding: 0;
        font: 600 0.625rem/1.2 var(--_font-body);
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }

      .provenance-summary-slot {
        display: inline-flex;
        align-items: center;
        gap: 0.375em;
      }

      .info-icon { width: 0.75rem; height: 0.75rem; opacity: 0.5; }

      .provenance-cik-link {
        font: 400 0.75rem/1.4 var(--_font-mono);
        color: var(--_focus);
        text-decoration: none;
        transition: opacity 0.15s ease;
      }

      .provenance-cik-link:hover { opacity: 0.8; }

      .provenance-content {
        display: flex;
        flex-direction: column;
        gap: 0.75em;
      }

      .provenance-header {
        font: 400 0.75rem/1.4 var(--_font-body);
        color: var(--_text-dim);
      }

      .provenance-filings {
        display: flex;
        flex-direction: column;
        gap: 0;
      }

      .filing-row {
        display: flex;
        align-items: center;
        gap: 1em;
        padding: 0.5em 0;
        border-bottom: 1px solid var(--_border);
        font: 400 0.75rem/1.4 var(--_font-body);
      }

      .filing-row:last-child { border-bottom: none; }

      .filing-quarter {
        font-weight: 500;
        color: var(--_text);
        min-width: 4em;
      }

      .filing-form {
        font: 400 0.6875rem/1 var(--_font-mono);
        color: var(--_text-dim);
        min-width: 4em;
      }

      .filing-accession {
        font: 400 0.6875rem/1 var(--_font-mono);
        color: var(--_text-dim);
      }

      .filing-link {
        display: inline-flex;
        align-items: center;
        gap: 0.25em;
        color: var(--_focus);
        text-decoration: none;
        transition: opacity 0.15s ease;
      }

      .filing-link svg { width: 0.75rem; height: 0.75rem; }
      .filing-link:hover { opacity: 0.8; }

      .filing-note {
        font: 400 0.75rem/1.4 var(--_font-body);
        color: var(--_text-dim);
        font-style: italic;
      }

      .provenance-disclaimer {
        font: 400 0.625rem/1.4 var(--_font-body);
        color: var(--_text-dim);
        padding-top: 0.5em;
        border-top: 1px solid var(--_border);
      }

      @media (max-width: 768px) {
        tfoot td { padding: 0.5em 0.75em; }

        .filing-row { flex-wrap: wrap; gap: 0.5em; }
        .filing-quarter, .filing-form { min-width: unset; }
        .filing-accession { display: none; }
      }

      .toolbar {
        display: flex;
        align-items: center;
        gap: 0.75em;
        margin-bottom: 0.75em;
      }

      .toolbar ft-table-filter { flex: 1; margin-bottom: 0; }

      .component-badge {
        margin-bottom: -1px;
        font: 600 0.6875rem/1 var(--_font-body);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--_text-dim);
        background: var(--_surface);
        padding: 0.3em 0.5em;
        border-radius: 3px;
        border: 1px solid var(--_border);
      }
    `;
  }

  private getTemplate(): string {
    const limit = this.visibleCount;
    const defaultLoginUrl = `https://auth.formthirteen.com/login?redirect_uri=${encodeURIComponent(window.location.href)}`;
    const ctaHref = this.getAttribute("cta-href") || defaultLoginUrl;

    const quarterLabel = formatQuarterLabel(this.getAttribute("q2"));

    return `
      <div class="container" ${limit > 0 ? `style="--visible-rows-height: ${limit * 40}px"` : ""}>
        <div class="toolbar">
          <ft-table-filter id="table-filter" categories="new,increase,decrease,exited" placeholder="Filter by name or CUSIP"></ft-table-filter>
        </div>

        ${quarterLabel ? `<span class="component-badge">${quarterLabel}</span>` : ""}
        <div class="tbl-wrap" role="region" aria-label="Portfolio holdings data">
          <table>
            <caption class="sr-only">Portfolio holdings sorted by percentage of portfolio</caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col" class="sortable" data-col="issuer" tabindex="0" role="button" aria-sort="none">
                  Issuer<span class="sort-icon" aria-hidden="true">↕</span>
                </th>
                <th scope="col" class="sortable num" data-col="marketValue" tabindex="0" role="button" aria-sort="none">
                  Value<span class="sort-icon" aria-hidden="true">↕</span>
                </th>
                <th scope="col" class="sortable num" data-col="valueChangePercent" tabindex="0" role="button" aria-sort="none">
                  Value Δ%<span class="sort-icon" aria-hidden="true">↕</span>
                </th>
                <th scope="col" class="sortable num" data-col="shares" tabindex="0" role="button" aria-sort="none">
                  Shares<span class="sort-icon" aria-hidden="true">↕</span>
                </th>
                <th scope="col" class="sortable num" data-col="shareChangePercent" tabindex="0" role="button" aria-sort="none">
                  Shares Δ%<span class="sort-icon" aria-hidden="true">↕</span>
                </th>
                <th scope="col" class="sortable num" data-col="changeAmount" tabindex="0" role="button" aria-sort="none">
                  Change $<span class="sort-icon" aria-hidden="true">↕</span>
                </th>
                <th scope="col" class="sortable num sort-desc" data-col="percentage" tabindex="0" role="button" aria-sort="descending">
                  % Portfolio<span class="sort-icon" aria-hidden="true">↓</span>
                </th>
              </tr>
            </thead>
            <tbody></tbody>
            <tfoot></tfoot>
          </table>
        </div>
        ${
          limit > 0
            ? `
        <div class="blur-overlay">
          <ft-signup-cta
            heading="Unlock full portfolio. Free signup. 30 seconds."
            subtext="No credit card. No trial period. Full portfolio access."
            button-text="View All Holdings"
            button-href="${ctaHref}"
          ></ft-signup-cta>
        </div>
        `
            : ""
        }
      </div>
    `;
  }
}

// Register custom element
if (!customElements.get("ft-holdings-table")) {
  customElements.define("ft-holdings-table", HoldingsTable);
}
